if (!customElements.get('photo-gallery')) {
  customElements.define('photo-gallery', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.dialog = this.querySelector('dialog');
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.addEventListener('click', event => {
        if(event.target.closest('[data-photo-apply]')){
          event.preventDefault();
          this.querySelector('photo-buyer-tools')?.synchronize();
          return;
        }
        const step = event.target.closest('[data-photo-step]');
        if (step) this.move(Number(step.dataset.photoStep));
        const button = event.target.closest('.photo-gallery__open') || event.target.closest('[data-gallery-title]')?.closest('.photo-gallery__card')?.querySelector('.photo-gallery__open');
        if (button) {
          if (this.dataset.preview && window.PhotoCatalog.enabled) {
            if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
            event.preventDefault();
          }
          this.open(button, true);
        }
        const purchase = event.target.closest('.photo-gallery__purchase, .photo-gallery__image-purchase');
        if (purchase && 'combinedPurchase' in purchase.dataset) return;
        if (purchase && this.dataset.preview && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
          event.preventDefault();
          if (this.reviewExpired()) {
            const notice = this.querySelector('.photo-gallery__image-status');
            notice.textContent = this.dataset.expiredLabel;
            notice.hidden = false;
            return;
          }
          this.navigatingToPrint = true;
          this.ownsHistory = false;
          history.replaceState({...history.state, photoReturnFocus:this.currentPhoto.dataset.photo}, '', location.href);
          this.dialog.close();
          location.assign(purchase.href);
        }
        if (purchase && this.purchasePending && !this.dataset.preview) {
          event.preventDefault();
          const information = this.querySelector('.photo-gallery__print-info');
          information.hidden = false;
          information.focus();
        }
      }, options);
      this.dialog.addEventListener('click', event => {
        if (event.target === this.dialog) {
          const box = this.dialog.getBoundingClientRect();
          if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) this.dialog.close();
        }
      }, options);
      this.dialog.addEventListener('close', () => {
        this.cancelNativeLoad();
        clearTimeout(this.hashTimer);
        this.imageVersion++;
        this.opener?.focus({preventScroll: true});
        if (this.navigatingToPrint) { this.navigatingToPrint = false; return; }
        if (this.ownsHistory && location.hash.startsWith('#photo=')) {
          this.ownsHistory = false;
          history.back();
        } else if (location.hash.startsWith('#photo=')) {
          history.replaceState(null, '', location.pathname + location.search);
        }
      }, options);
      this.dialog.addEventListener('keydown', event => {
        if ((event.key === 'ArrowLeft' || event.key === 'ArrowRight') && !event.altKey && !event.ctrlKey && !event.metaKey && !event.target.closest('input, textarea, select, [contenteditable]')) {
          event.preventDefault();
          this.move(event.key === 'ArrowLeft' ? -1 : 1);
          return;
        }
        if (event.key !== 'Tab') return;
        const controls = [...this.dialog.querySelectorAll('button, a[href], input, select, textarea, [tabindex]')].filter(item => !item.disabled && item.tabIndex >= 0 && item.getClientRects().length);
        const first = controls[0];
        const last = controls.at(-1);
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }, options);
      window.addEventListener('popstate', async () => {
        this.cancelNativeLoad();
        clearTimeout(this.hashTimer);
        this.ownsHistory = false;
        if (!location.hash.startsWith('#photo=') && this.dialog.open) this.dialog.close();
        else { await this.querySelector('photo-buyer-tools')?.restoreFromUrl(); this.openFromHash(); }
      }, options);
      window.addEventListener('pageshow', () => {
        // History restores can retain the select value used to leave this page.
        const picker = this.querySelector('.photo-gallery__review-picker select');
        if (picker) picker.value = this.dataset.scope;
        if (!new URLSearchParams(location.search).has('review_print')) {
          const tools=this.querySelector('photo-buyer-tools');
          Promise.resolve(tools?.restoreFromUrl()).then(()=>{this.openFromHash();this.restoreReturnFocus();});
        }
      }, options);
      if (this.dataset.collageViewer === 'true') this.openFromHash();
      else if (this.dataset.preview) this.loadReview();
      else {
        this.arrange();
        this.buyerReady = true;
        this.dispatchEvent(new CustomEvent('photo-gallery:ready',{bubbles:true}));
        this.openFromHash();
      }
    }
    disconnectedCallback() {
      this.cancelNativeLoad();
      clearTimeout(this.hashTimer);
      this.abort?.abort();
      if (this.dialog?.open) this.dialog.close();
      this.initialized = false;
    }
    openFromHash() {
      if (!location.hash.startsWith('#photo=')) return;
      const tools = this.querySelector('photo-buyer-tools');
      if (tools && !tools.filtersReady) return;
      let handle;
      try { handle = decodeURIComponent(location.hash.slice(7)); } catch { return; }
      const scope = this.dataset.collageViewer === 'true' ? this.closest('photo-collage') : this;
      const button = [...scope.querySelectorAll('.photo-gallery__open')].find(item => item.dataset.photo === handle);
      if (button) this.open(button, false);
      else if (this.nativePagination()) {
        const requestedHash = location.hash;
        const loadVersion = this.nativeLoadVersion;
        this.loadNativeSequence().then(sequence => {
          if (loadVersion !== this.nativeLoadVersion || location.hash !== requestedHash || this.dialog.open) return;
          const selected = sequence.find(item => item.dataset.photo === handle);
          if (selected) this.open(selected, false);
        }).catch(() => {});
      }
    }
    arrange() {
      const grid = this.querySelector('.photo-gallery__grid');
      const cards = [...grid.querySelectorAll('.photo-gallery__card')];
      const draft = window.PhotoCatalog.enabled && !!this.dataset.preview && !!this.review;
      const sourceOrder = draft ? new Map(this.review.photos.map((photo,index) => [photo.id,index])) : null;
      const baseline = sourceOrder ? [...cards].sort((a,b) => sourceOrder.get(a.querySelector('.photo-gallery__open').dataset.id) - sourceOrder.get(b.querySelector('.photo-gallery__open').dataset.id)) : cards;
      let ordered = ['landscape','portrait','square'].flatMap(orientation => baseline.filter(card => card.dataset.orientation === orientation));
      const saved = this.querySelector('photo-buyer-tools')?.favoriteMode || new URLSearchParams(location.search).get('view_favorites') === '1';
      const coverId = draft && !saved && this.dataset.scope !== 'all' ? this.review.collections.find(collection => collection.handle === this.dataset.scope)?.firstPhotoId : null;
      const cover = coverId && ordered.find(card => !card.hidden && card.querySelector('.photo-gallery__open').dataset.id === coverId);
      if (cover) ordered = [cover,...ordered.filter(card => card !== cover)];
      const visibleOrientations = new Set();
      for (const card of ordered) {
        const first = !card.hidden && !visibleOrientations.has(card.dataset.orientation);
        card.classList.toggle('photo-gallery__card--row-start',first);
        if (!card.hidden) visibleOrientations.add(card.dataset.orientation);
      }
      if(ordered.some((card,index)=>cards[index]!==card))for(const card of ordered)grid.append(card);
      const pagination = grid.querySelector('.photo-gallery__pagination');
      if (pagination && grid.lastElementChild!==pagination) grid.append(pagination);
    }
    reviewExpired() {
      return window.PhotoCatalog.review && !!this.dataset.preview && Number.isFinite(this.reviewExpiresAt) && Date.now() > this.reviewExpiresAt;
    }
    nativePagination() {
      if (this.dataset.preview || this.dataset.collageViewer === 'true') return null;
      const pagination = this.querySelector('[data-gallery-page-count]');
      return pagination && Number(pagination.dataset.galleryPageCount) > 1 ? pagination : null;
    }
    nativeKey() {
      const url = new URL(location.href);
      for (const key of ['find','orientation','color','view_favorites','view_print_guide','guide_photo','guide_size']) url.searchParams.delete(key);
      return url.pathname + url.search + '|' + this.dataset.scope;
    }
    cancelNativeLoad() {
      this.nativeLoadVersion = (this.nativeLoadVersion || 0) + 1;
      this.sequenceAbort?.abort();
      this.sequencePromise = null;
      this.sequenceLoading = false;
    }
    async loadNativeSequence() {
      const key = this.nativeKey();
      if (this.nativeCache?.key === key) return this.nativeCache.items;
      if (this.sequencePromise) return this.sequencePromise;
      const pagination = this.nativePagination();
      if (!pagination || !this.dataset.sectionId) throw new Error('Collection context unavailable');
      const count = Number(pagination.dataset.galleryPageCount);
      const current = Number(pagination.dataset.galleryCurrentPage);
      if (!Number.isSafeInteger(count) || !Number.isSafeInteger(current) || current < 1 || current > count) throw new Error('Collection pagination unavailable');
      const controller = new AbortController();
      this.sequenceAbort = controller;
      const task = async () => {
        const pages = new Map([[current, this.visibleItems(this.querySelector('.photo-gallery__grid'))]]);
        for (let page = 1; page <= count; page++) {
          if (page === current) continue;
          const url = new URL(location.href);
          url.hash = '';
          url.searchParams.set(pagination.dataset.galleryPageParam || 'page', page);
          url.searchParams.set('sections', this.dataset.sectionId);
          const response = await fetch(url.pathname + url.search, {signal:controller.signal, credentials:'same-origin'});
          if (!response.ok) throw new Error('Collection page unavailable');
          const sections = await response.json();
          const html = sections[this.dataset.sectionId];
          if (typeof html !== 'string' || !html) throw new Error('Collection section unavailable');
          const template = document.createElement('template');
          template.innerHTML = html;
          const gallery = template.content.querySelector('photo-gallery');
          const pageInfo = gallery?.querySelector('[data-gallery-page-count]');
          if (!gallery || gallery.dataset.sectionId !== this.dataset.sectionId || gallery.dataset.scope !== this.dataset.scope || Number(pageInfo?.dataset.galleryPageCount) !== count || Number(pageInfo?.dataset.galleryCurrentPage) !== page) throw new Error('Collection context changed');
          pages.set(page, this.visibleItems(gallery.querySelector('.photo-gallery__grid')));
        }
        if (controller.signal.aborted || this.nativeKey() !== key) throw new DOMException('Cancelled', 'AbortError');
        const seen = new Set();
        const sequence = [...pages].sort((a,b) => a[0]-b[0]).flatMap(([,items]) => items).filter(item => {
          if (!item.dataset.photo || !item.dataset.image || seen.has(item.dataset.photo)) return false;
          seen.add(item.dataset.photo);
          return true;
        });
        const order = ['landscape','portrait','square'];
        sequence.sort((a,b) => order.indexOf(a.closest('.photo-gallery__card').dataset.orientation) - order.indexOf(b.closest('.photo-gallery__card').dataset.orientation));
        this.nativeCache = {key, items:sequence};
        return sequence;
      };
      const promise = task();
      this.sequencePromise = promise;
      try { return await promise; }
      finally { if (this.sequencePromise === promise) this.sequencePromise = null; }
    }
    prepareNativeSequence() {
      if (this.querySelector('photo-buyer-tools')?.hasActiveFilters() || !this.nativePagination() || this.nativeCache?.key === this.nativeKey()) return;
      const handle = this.sequence[this.currentIndex].dataset.photo;
      this.sequenceLoading = true;
      this.navigationFailed = false;
      this.updateNavigation();
      const loadVersion = this.nativeLoadVersion;
      this.loadNativeSequence().then(sequence => {
        if (loadVersion !== this.nativeLoadVersion || !this.dialog.open || this.sequenceAbort?.signal.aborted) return;
        this.sequence = sequence;
        this.currentIndex = sequence.findIndex(item => item.dataset.photo === handle);
        this.sequenceLoading = false;
        this.updateNavigation();
      }).catch(error => {
        if (loadVersion !== this.nativeLoadVersion || error.name === 'AbortError' || !this.dialog.open) return;
        this.sequenceLoading = false;
        this.navigationFailed = true;
        this.updateNavigation();
      });
    }
    visibleItems(scope) {
      return [...scope.querySelectorAll('.photo-gallery__open')].filter(item => !item.closest('[hidden]'));
    }
    items() {
      const tools = this.querySelector('photo-buyer-tools');
      if (tools?.hasActiveFilters() && tools.grid) return tools.currentItems();
      if (this.nativePagination() && this.nativeCache?.key === this.nativeKey()) return this.nativeCache.items;
      const scope = this.dataset.collageViewer === 'true' ? this.closest('photo-collage') : this.querySelector('.photo-gallery__grid');
      return this.visibleItems(scope);
    }
    open(button, pushHistory) {
      if (this.reviewExpired()) return;
      const tools=this.querySelector('photo-buyer-tools');
      if(!this.dialog.open&&tools?.filtersPending()){
        const handle=button.dataset.photo;
        tools.synchronize().then(ready=>{
          if(!ready||!this.isConnected||this.dialog.open)return;
          const selected=this.items().find(item=>item.dataset.photo===handle);
          if(selected)this.open(selected,pushHistory);
        });
        return;
      }
      if (this.dataset.preview && window.PhotoCatalog.enabled) {
        const destination = this.printUrl(button.dataset.photo);
        if (pushHistory) {
          history.replaceState({...history.state,photoReturnFocus:button.dataset.photo},'',location.href);
          location.assign(destination);
        } else location.replace(destination);
        return;
      }
      if (!this.dialog.open) {
        this.opener = button.isConnected ? button : this.querySelector('.photo-gallery__grid .photo-gallery__open');
        this.navigationFailed = false;
        this.sequence = this.items();
        this.imageVersion = 0;
      }
      this.currentIndex = this.sequence.findIndex(item => item.dataset.photo === button.dataset.photo);
      if (this.currentIndex < 0) return;
      this.renderPhoto(button);
      if (!this.dialog.open) {
        this.dialog.showModal();
        this.querySelector('.photo-gallery__close').focus();
        this.prepareNativeSequence();
      }
      if (pushHistory) {
        history.pushState(null, '', '#photo=' + encodeURIComponent(button.dataset.photo));
        this.ownsHistory = true;
        this.lastHashWrite = performance.now();
      }
    }
    move(step) {
      if (!this.dialog.open || this.reviewExpired() || this.sequenceLoading) return;
      const next = this.currentIndex + step;
      if (next < 0 || next >= this.sequence.length) return;
      this.currentIndex = next;
      const button = this.sequence[next];
      const focused = document.activeElement;
      this.renderPhoto(button);
      clearTimeout(this.hashTimer);
      const updateHash = () => {
        if (!this.dialog.open) return;
        history.replaceState(history.state, '', '#photo=' + encodeURIComponent(this.sequence[this.currentIndex].dataset.photo));
        this.lastHashWrite = performance.now();
      };
      const delay = Math.max(0, 350 - (performance.now() - (this.lastHashWrite || 0)));
      if (delay) this.hashTimer = setTimeout(updateHash, delay);
      else updateHash();
      if (focused.disabled) this.querySelector(next === 0 ? '[data-photo-step="1"]' : '[data-photo-step="-1"]').focus();
    }
    renderPhoto(button) {
      this.currentPhoto = button;
      const version = ++this.imageVersion;
      const previous = this.querySelector('.photo-gallery__large');
      const image = previous.cloneNode(false);
      image.removeAttribute('src');
      image.style.visibility = 'hidden';
      previous.replaceWith(image);
      const notice = this.querySelector('.photo-gallery__image-status');
      notice.hidden = true;
      const stage = this.querySelector('.photo-gallery__stage');
      stage.setAttribute('aria-busy', 'true');
      const complete = failed => {
        if (version !== this.imageVersion || !image.isConnected) return;
        stage.removeAttribute('aria-busy');
        image.style.visibility = failed ? 'hidden' : '';
        notice.hidden = !failed;
        notice.textContent = failed ? this.dataset.imageErrorLabel : '';
      };
      image.addEventListener('load', () => complete(false), {once:true});
      image.addEventListener('error', () => complete(true), {once:true});
      this.webImage(image, {image:button.dataset.image,width:Number(button.dataset.width)||2400}, '100vw', 2400);
      if (image.complete) complete(!image.naturalWidth);
      image.alt = button.dataset.alt || button.dataset.title;
      image.removeAttribute('width');
      image.removeAttribute('height');
      this.querySelector('.photo-gallery__caption h2').textContent = button.dataset.title;
      const description = this.querySelector('.photo-gallery__description');
      // Parse canonical rich text in an inert template, then display only its plain text.
      // This decodes entities without inserting product HTML into the live document.
      if (button.dataset.descriptionHtml) {
        const content = document.createElement('template');
        content.innerHTML = button.dataset.descriptionHtml;
        description.textContent = content.content.textContent.trim();
      } else description.textContent = button.dataset.description || '';
      window.PhotoRecognition?.append(description, this.review?.photos.find(photo => photo.id === button.dataset.photo || photo.handle === button.dataset.photo)?.recognition, this);
      description.hidden = !description.textContent.trim();
      this.purchasePending = button.dataset.purchasable !== 'true' || !!this.dataset.preview;
      this.updatePurchaseLinks(button);
      this.querySelector('.photo-gallery__destination').textContent = this.purchasePending ? button.dataset.productUrl : '';
      this.querySelector('.photo-gallery__print-info').hidden = true;
      this.updateNavigation();
      this.dispatchEvent(new CustomEvent('photo-gallery:photo',{bubbles:true,detail:{photo:button}}));
    }
    updatePurchaseLinks(button) {
      const destination = this.dataset.preview ? this.printUrl(button.dataset.photo) : this.purchasePending ? '#print-information' : button.dataset.productUrl;
      this.querySelector('.photo-gallery__purchase').href = destination;
      const imageLink = this.querySelector('.photo-gallery__image-purchase');
      if (imageLink) {
        imageLink.href = destination;
        imageLink.setAttribute('aria-label',imageLink.dataset.purchaseLabel.replace('[title]',button.dataset.title));
      }
    }
    updateNavigation() {
      const previousButton = this.querySelector('[data-photo-step="-1"]');
      const next = this.querySelector('[data-photo-step="1"]');
      previousButton.disabled = this.sequenceLoading || this.currentIndex === 0;
      next.disabled = this.sequenceLoading || this.currentIndex === this.sequence.length - 1;
      const position = this.querySelector('.photo-gallery__position');
      if (this.sequenceLoading) position.textContent = this.dataset.loadingLabel;
      else {
        position.textContent = (this.navigationFailed ? this.dataset.positionPageLabel : this.dataset.positionLabel).replace('[current]', this.currentIndex + 1).replace('[total]', this.sequence.length);
        if (this.navigationFailed) position.textContent += '. ' + this.dataset.navigationErrorLabel;
      }
    }
    async loadReview() {
      try {
        const review = await window.PhotoCatalog.load(this.dataset.preview);
        this.review = review;
        this.reviewExpiresAt = Date.parse(review.expiresAt);
        if (window.PhotoCatalog.review && (!Number.isFinite(this.reviewExpiresAt) || this.reviewExpired())) throw new Error('Review expired');
        if (this.showReviewPage(review)) return;
        const requested = new URLSearchParams(location.search).get('review_collection') || this.dataset.scope;
        const requestedCollection = review.collections.find(collection => collection.handle === requested || collection.legacyHandles?.includes(requested));
        if (requestedCollection) {
          this.dataset.scope = requestedCollection.handle;
          this.querySelector('.photo-gallery__heading').textContent = requestedCollection.title;
        }
        if (requestedCollection?.subtitle) {
          const subtitle = document.createElement('p');
          subtitle.className = 'photo-gallery__collection-subtitle';
          subtitle.textContent = requestedCollection.subtitle;
          this.querySelector('.photo-gallery__heading').after(subtitle);
        }
        const grid = this.querySelector('.photo-gallery__grid');
        const picker = this.querySelector('.photo-gallery__review-picker select');
        if (picker) {
          if (!window.PhotoCatalog.review) {
            picker.closest('label').firstChild.textContent = window.PhotoCatalog.labels.collection + ' ';
            if (window.PhotoCatalog.preview) {
              const mode = document.createElement('input');
              mode.type = 'hidden';
              mode.name = 'catalog';
              mode.value = 'live';
              picker.form.append(mode);
            }
          }
          for (const collection of review.collections || []) {
            const option = document.createElement('option');
            option.value = collection.handle;
            option.textContent = collection.title;
            picker.append(option);
          }
          picker.value = this.dataset.scope;
          // Navigate out of saved-only/search views; the native submit button also
          // works when a browser does not emit a select change event.
          picker.addEventListener('change', () => picker.form.requestSubmit(), {signal:this.abort.signal});
        }
        for (const photo of review.photos) {
          const figure = document.createElement('figure');
          figure.className = 'photo-gallery__card';
          figure.dataset.collections = photo.collections.join(',');
          figure.dataset.orientation = photo.width > photo.height ? 'landscape' : photo.width < photo.height ? 'portrait' : 'square';
          figure.hidden = this.dataset.scope !== 'all' && !photo.collections.includes(this.dataset.scope);
          const button = document.createElement('a');
          button.href = this.printUrl(photo.handle);
          button.className = 'photo-gallery__open';
          button.setAttribute('aria-label', this.dataset.openLabel + ': ' + photo.title);
          Object.assign(button.dataset, {photo:photo.handle, id:photo.id, title:photo.title, alt:photo.alt || photo.title, description:photo.description || '', image:photo.image, width:String(photo.width), height:String(photo.height), productUrl:'/products/' + photo.handle, printUrl:this.printUrl(photo.handle), purchasable:'false'});
          const image = document.createElement('img');
          this.webImage(image, photo, '(min-width:750px) 46vw, 100vw');
          image.alt = photo.alt || photo.title;
          image.width = photo.width;
          image.height = photo.height;
          image.loading = 'lazy';
          image.decoding = 'async';
          const caption = document.createElement('figcaption');
          const titleButton = document.createElement('button');
          titleButton.type = 'button';
          titleButton.className = 'photo-gallery__title';
          titleButton.dataset.galleryTitle = '';
          titleButton.textContent = photo.title;
          caption.append(titleButton);
          button.append(image);
          figure.append(button, caption);
          grid.append(figure);

        }
        const reviewNotice = this.querySelector('.photo-gallery__review');
        reviewNotice.hidden = !window.PhotoCatalog.review;
        if (window.PhotoCatalog.review) reviewNotice.textContent += ' (' + review.generatedAt.slice(0,10) + ')';
        if (!window.PhotoCatalog.review && !grid.children.length) {
          const empty = document.createElement('p');
          empty.textContent = window.PhotoCatalog.labels.empty;
          grid.append(empty);
        }
        this.arrange();
        this.buyerReady = true;
        this.dispatchEvent(new CustomEvent('photo-gallery:ready',{bubbles:true}));
        if (!this.showRequestedPrint()) this.openFromHash();
        window.PhotoRecognition?.ready(this);
      } catch (error) {
        if (error.name !== 'AbortError') {
          const notice = this.querySelector('.photo-gallery__review');
          notice.hidden = false;
          notice.textContent = window.PhotoCatalog.review ? this.dataset.expiredLabel : window.PhotoCatalog.labels.unavailable;
        }
      }
    }
    recognitionSummary(template) {
      // The evidence registry drives both the About list and its distinct-photograph counts.
      const records = this.review.recognitionRecords || [];
      const handles = label => new Set(records.filter(record => label(record.label)).map(record => record.handle));
      const master = handles(label => label === 'LFI — Leica Master Shot');
      const category = handles(label => label.startsWith('LFI — selected for '));
      const awarded = handles(label => label === '1x.com — Awarded');
      const published = handles(label => label === '1x.com — Published');
      const numberWords = value => {
        const small = ['zero','one','two','three','four','five','six','seven','eight','nine','ten','eleven','twelve','thirteen','fourteen','fifteen','sixteen','seventeen','eighteen','nineteen'];
        if (value < 20) return small[value];
        if (value < 100) return ['','','twenty','thirty','forty','fifty','sixty','seventy','eighty','ninety'][Math.floor(value / 10)] + (value % 10 ? '-' + small[value % 10] : '');
        return String(value);
      };
      const publishedTotal = new Set([...awarded, ...published]).size;
      const counts = {
        lfiTotal: new Set([...master, ...category]).size,
        lfiMaster: master.size + ' Leica Master Shot' + (master.size === 1 ? '' : 's'),
        lfiCategory: category.size + ' category selection' + (category.size === 1 ? '' : 's'),
        oneXTotal: publishedTotal,
        oneXTotalWords: numberWords(publishedTotal).replace(/^./, letter => letter.toUpperCase()),
        oneXAwardedWords: numberWords(awarded.size),
        oneXAwarded: awarded.size,
        oneXPublishedOnly: [...published].filter(handle => !awarded.has(handle)).length
      };
      return template.replace(/\{\{(\w+)\}\}/g, (token, key) => counts[key] ?? token);
    }
    showReviewPage(review) {
      const page = review.pages?.find(item => item.handle === 'about-the-photographer' && item.sourceIsPublished === false);
      if (!page || !Array.isArray(page.paragraphs) || page.paragraphs.length !== 5) return false;
      const destination = new URL(this.dataset.galleryUrl, location.origin);
      if (window.PhotoCatalog.preview) destination.searchParams.set('preview_theme_id', this.dataset.previewTheme);
      destination.searchParams.set('review_page', page.handle);
      const link = document.createElement('a');
      link.href = destination.pathname + destination.search;
      link.textContent = page.title;
      link.className = 'photo-print__back';
      const navigation = document.createElement('p');
      navigation.className = 'photo-gallery__bio-link';
      navigation.append(link);
      this.querySelector('.photo-gallery__heading').before(navigation);
      if (new URLSearchParams(location.search).get('review_page') !== page.handle) return false;
      for (const selector of ['.photo-gallery__heading', '.photo-gallery__review', '.photo-gallery__review-picker', '.photo-gallery__grid', 'photo-buyer-tools', '.photo-gallery__bio-link']) {
        const element = this.querySelector(selector);
        if (element) element.hidden = true;
      }
      const article = document.createElement('article');
      article.className = 'photo-gallery__bio';
      article.dataset.pageId = page.id;
      const back = this.querySelector('.photo-print-template').content.querySelector('.photo-print__back').cloneNode(true);
      destination.searchParams.delete('review_page');
      destination.hash = 'photographs';
      back.href = destination.pathname + destination.search + destination.hash;
      const title = document.createElement('h1');
      title.className = 'photo-print__title';
      title.textContent = page.title;
      title.id = 'PhotoReviewPageTitle-' + this.id;
      title.tabIndex = -1;
      article.setAttribute('aria-labelledby', title.id);
      article.append(back, title);
      const layout = document.createElement('div');
      layout.className = 'photo-gallery__bio-layout';
      if (page.portrait?.url && page.portrait.width > 0 && page.portrait.height > 0) {
        const figure = document.createElement('figure');
        figure.className = 'photo-gallery__bio-portrait';
        const image = document.createElement('img');
        image.src = page.portrait.url;
        image.width = page.portrait.width;
        image.height = page.portrait.height;
        image.alt = page.portrait.alt || page.title;
        image.decoding = 'async';
        figure.append(image);
        layout.append(figure);
      }
      const copy = document.createElement('div');
      copy.className = 'photo-gallery__bio-copy';
      for (const [index, source] of page.paragraphs.entries()) {
        const text = index === (page.recognitionParagraphIndex ?? 3) ? this.recognitionSummary(source) : source;
        const paragraph = document.createElement('p');
        paragraph.className = 'photo-print__description';
        const links = (page.profileLinks || []).map(profile => ({ ...profile, position: text.indexOf(profile.label) })).filter(profile => profile.position >= 0).sort((a, b) => a.position - b.position);
        let cursor = 0;
        for (const profile of links) {
          paragraph.append(document.createTextNode(text.slice(cursor, profile.position)));
          const link = document.createElement('a');
          link.className = 'photo-gallery__bio-profile';
          link.href = profile.url;
          link.target = '_blank';
          link.rel = 'noopener';
          link.textContent = profile.label;
          paragraph.append(link);
          cursor = profile.position + profile.label.length;
        }
        paragraph.append(document.createTextNode(text.slice(cursor)));
        copy.append(paragraph);
        if (index === (page.recognitionParagraphIndex ?? 3)) window.PhotoRecognition?.append(copy, review.recognitionRecords, this, true);
      }
      layout.append(copy);
      article.append(layout);
      this.append(article);
      document.title = page.title;
      window.PhotoRecognition?.ready(this);
      return true;
    }
    contextUrl(home = false) {
      const url = new URL(home ? this.dataset.homeUrl : this.dataset.galleryUrl, location.origin);
      const source = new URLSearchParams(location.search);
      for (const key of ['find','orientation','color','view_favorites','review_collection']) {
        if (source.has(key)) url.searchParams.set(key,source.get(key));
      }
      if (window.PhotoCatalog.preview) url.searchParams.set('preview_theme_id',this.dataset.previewTheme);
      if (window.PhotoCatalog.preview && !window.PhotoCatalog.review) url.searchParams.set('catalog','live');
      if (!home && this.dataset.scope !== 'all') url.searchParams.set('review_collection',this.dataset.scope);
      return url;
    }
    printUrl(handle) {
      const url = this.contextUrl();
      url.searchParams.set('review_print',handle);
      url.searchParams.set('review_return',location.pathname === this.dataset.homeUrl || new URLSearchParams(location.search).get('review_return') === 'home' ? 'home' : 'gallery');
      return url.pathname + url.search;
    }
    returnUrl(handle) {
      const url = this.contextUrl(new URLSearchParams(location.search).get('review_return') === 'home');
      url.searchParams.set('review_focus',handle);
      url.hash = 'photographs';
      return url.pathname + url.search + url.hash;
    }
    restoreReturnFocus() {
      if (location.hash.startsWith('#photo=') || new URLSearchParams(location.search).has('review_print')) return;
      const handle=new URLSearchParams(location.search).get('review_focus') || history.state?.photoReturnFocus;
      const scope=this.dataset.collageViewer === 'true' ? this.closest('photo-collage') : this;
      const button=[...scope.querySelectorAll('.photo-gallery__open')].find(item=>item.dataset.photo===handle && !item.closest('[hidden]'));
      if(button)button.focus();
    }
    webImage(image, photo, sizes, limit = 1600) {
      const url = new URL(photo.image,location.origin);
      const width = Math.min(Number(photo.width)||limit,limit);
      const rendition = value => { const copy=new URL(url);copy.searchParams.set('width',value);return copy.href; };
      image.src = rendition(width);
      image.srcset = [...new Set([400,800,1200,1600,2400,width].filter(value=>value<=width))].sort((a,b)=>a-b).map(value=>rendition(value)+' '+value+'w').join(', ');
      image.sizes = sizes;
    }
    createPrintPage(photo) {
      const template = this.querySelector('.photo-print-template');
      if (!template) return;
      const page = template.content.firstElementChild.cloneNode(true);
      page.dataset.photo = photo.handle;
      page.dataset.id = photo.id;
      page.dataset.orientation = photo.width === photo.height ? 'square' : photo.width < photo.height ? 'portrait' : 'landscape';
      const image = page.querySelector('.photo-print__image');
      this.webImage(image,photo,'(min-width:750px) 60vw, 100vw');
      Object.assign(image, {alt:photo.alt || photo.title, width:photo.width, height:photo.height});
      const viewer=page.querySelector('photo-print-viewer');
      viewer.dataset.image=photo.image;viewer.dataset.width=photo.width;viewer.dataset.height=photo.height;
      page.querySelector('.photo-print__title').textContent = photo.title;
      page.querySelector('.photo-print__description').textContent = photo.description || '';
      window.PhotoRecognition?.append(page.querySelector('.photo-print__description'), photo.recognition, this);
      const sizes = window.PhotoPrintOptions?.supplierSizes(photo) || photo.printSizes || [];
      const formatter = new Intl.NumberFormat(document.documentElement.lang || 'en', {maximumFractionDigits: 2});
      const inches = values => values.map(value => formatter.format(value)).join(' × ') + ' ' + page.dataset.inches;
      const updateDimensions = size => {
        page.querySelector('.photo-print__paper-size').textContent = inches(size.paper);
        page.querySelector('.photo-print__image-size').textContent = inches(size.image);
        page.querySelector('.photo-print__border-horizontal').textContent = inches([size.border[0]]);
        page.querySelector('.photo-print__border-vertical').textContent = inches([size.border[1]]);
        page.querySelector('.photo-print__source-review').hidden = !size.sourceReview;
        page.dispatchEvent(new CustomEvent('photo-print:dimensions',{bubbles:true,detail:{photo,size}}));
      };
      page.querySelector('.photo-print__dimensions').hidden = !sizes.length;
      const grid = page.querySelector('.photo-print__size-grid');
      for (const [index, size] of sizes.entries()) {
        const label = document.createElement('label');
        const input = document.createElement('input');
        input.type = 'radio';
        input.name = 'review-size-' + this.id + '-' + photo.handle;
        input.value = size.value;
        input.checked = index === 1;
        const text = document.createElement('span');
        text.textContent = inches(size.paper);
        input.addEventListener('change', () => {
          if (input.checked) updateDimensions(size);
        }, {signal: this.abort.signal});
        if (input.checked) updateDimensions(size);
        label.append(input, text);
        grid.append(label);
      }
      const back = page.querySelector('.photo-print__back');
      back.href = this.returnUrl(photo.handle);
      if (window.PhotoCatalog.enabled && new URLSearchParams(location.search).get('review_return') === 'home') back.textContent = back.dataset.homeLabel;
      this.querySelector('.photo-print-pages').append(page);
      this.dispatchEvent(new CustomEvent('photo-print:created',{bubbles:true,detail:{page,photo,size:sizes[Math.min(1,sizes.length-1)]}}));
    }
    showRequestedPrint() {
      let requested = new URLSearchParams(location.search).get('review_print');
      if (!requested && window.PhotoCatalog.enabled && this.dataset.preview && location.hash.startsWith('#photo=')) {
        try { requested = decodeURIComponent(location.hash.slice(7)); } catch { return false; }
        if (!this.review?.photos.some(photo => photo.handle === requested)) return false;
        const canonical = new URL(this.printUrl(requested), location.href);
        const recognition = new URLSearchParams(location.search).get('recognition');
        if (recognition) canonical.searchParams.set('recognition', recognition);
        history.replaceState(history.state,'',canonical);
      }
      if (!requested || !this.dataset.preview) return false;
      const pages = this.querySelector('.photo-print-pages');
      let page = [...pages.children].find(item => item.dataset.photo === requested);
      if (!page) {
        const photo=this.review?.photos.find(item=>item.handle===requested);
        if(photo) {this.createPrintPage(photo);page=[...pages.children].find(item=>item.dataset.photo===requested);}
      }
      if (!page) return false;
      for (const selector of ['.photo-gallery__heading','.photo-gallery__review','.photo-gallery__review-picker','photo-buyer-tools','.photo-gallery__grid']) {
        const element = this.querySelector(selector);
        if (element) element.hidden = true;
      }
      pages.hidden = false;
      page.hidden = false;
      page.querySelector('.photo-print__image').loading = 'eager';
      document.title = page.querySelector('.photo-print__title').textContent + ' — Print review';
      window.PhotoCombined?.show(this,page);
      return true;
    }
  });
}




